import {
    Box,
    Card,
    Grid,
    Button,
    Select,
    MenuItem,
    FormHelperText,
} from '@mui/material';
import { H2, H6 } from 'components/Typography';
import FlexBox from 'components/flexbox/FlexBox';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from 'utils/axios';
import Scrollbar from 'components/ScrollBar';
import AppTextField from 'components/input-fields/AppTextField';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const InvoiceEdit = () => {
    const navigate = useNavigate();

    const { id } = useParams();

    const [packageList, setPackageList] = useState([]);
    const [workshopList, setWorkshopList] = useState([]);
    const [selectCount, setSelectCount] = useState(0);

    const initialValues = useMemo(() => {
        return {
            fullName: '',
            id: '',
            number: '',
            package: '',
            total_price: '',
            workshopIds: [],
        };
    }, []);
    const fieldValidationSchema = Yup.object().shape({
        fullName: Yup.string(),
        id: Yup.string(),
        number: Yup.string(),
        package: Yup.string(),
        total_price: Yup.number(),
        workshopIds: Yup.array().of(Yup.string()),
    });
    const {
        values,
        errors,
        handleChange,
        handleSubmit,
        touched,
        setValues,
        isSubmitting,
    } = useFormik({
        initialValues,
        validationSchema: fieldValidationSchema,
        onSubmit: async (values, { setSubmitting }) => {
            await axiosInstance
                .patch(`api/invoice`, values, {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem(
                            'accessToken',
                        )}`,
                    },
                })
                .then(() => navigate('/dashboard/operator/invoice-list'))
                .catch((err) => console.log(err));
        },
    });

    const getData = async () => {
        await axiosInstance
            .get('api/invoice/' + id)
            .then(({ data }) => {
                setSelectCount(data.workshop_details.length);
                setValues({
                    fullName: data.user.full_name,
                    id: data.id,
                    number: data.number,
                    package: data.package.id,
                    total_price: data.total_price,
                    workshopIds: data.workshop_details.map(
                        (workshopDetail) => workshopDetail.workshop.id,
                    ),
                });
            })
            .catch((err) => console.log(err));

        await axiosInstance
            .get(`api/packages/byInvoice/${id}`)
            .then(({ data }) => {
                setPackageList(data);
            })
            .catch((err) => console.log(err));
        await axiosInstance
            .get(`api/workshops`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem(
                        'accessToken',
                    )}`,
                },
            })
            .then(({ data }) => {
                setWorkshopList(data);
            })
            .catch((err) => console.log(err));
    };

    useEffect(() => {
        getData();
    }, []);

    const handleRemoveSelect = (index) => {
        setValues((prevValues) => {
            const updatedWorkshopIds = [...prevValues.workshopIds];
            updatedWorkshopIds.splice(index, 1);
            return { ...prevValues, workshopIds: updatedWorkshopIds };
        });

        setSelectCount((prevCount) => Math.max(prevCount - 1, 1));
    };

    const handleAddSelect = () => {
        setSelectCount((prevCount) => prevCount + 1);
    };

    return (
        <Box pt={2} pb={4}>
            <Card
                sx={{
                    padding: 2,
                }}
            >
                <H2>Edit Invoice</H2>
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <H6 mb={1}>Invoice Number</H6>
                            <AppTextField
                                fullWidth
                                size="small"
                                name="number"
                                placeholder="Name"
                                value={values.number}
                                disabled
                                onChange={handleChange}
                                error={Boolean(errors.number && touched.number)}
                                helperText={touched.number && errors.number}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <H6 mb={1}>Full Name</H6>
                            <AppTextField
                                fullWidth
                                size="small"
                                name="fullName"
                                placeholder="Name"
                                value={values.fullName}
                                disabled
                                onChange={handleChange}
                                error={Boolean(
                                    errors.fullName && touched.fullName,
                                )}
                                helperText={touched.fullName && errors.fullName}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <H6 mb={1}>Package</H6>
                            <Select
                                size="small"
                                variant="outlined"
                                name={'package'}
                                fullWidth
                                value={values.package}
                                onChange={handleChange}
                            >
                                {packageList &&
                                    packageList.map((item, key) => (
                                        <MenuItem key={key} value={item.id}>
                                            {item.name}
                                        </MenuItem>
                                    ))}
                            </Select>
                            {touched.package && (
                                <FormHelperText
                                    error
                                    sx={{
                                        ml: '14px',
                                        fontSize: '0.75rem',
                                        fontWeight: 400,
                                    }}
                                >
                                    {errors.package}
                                </FormHelperText>
                            )}
                        </Grid>
                        <Grid item xs={6}>
                            <H6 mb={1}>Total Price</H6>
                            <AppTextField
                                fullWidth
                                size="small"
                                name="total_price"
                                placeholder="Name"
                                value={values.total_price}
                                onChange={handleChange}
                                error={Boolean(
                                    errors.total_price && touched.total_price,
                                )}
                                helperText={
                                    touched.total_price && errors.total_price
                                }
                            />
                        </Grid>
                        <Grid item container xs={12} spacing={2}>
                            <Grid item xs={12}>
                                <H6 mb={1}>Workshop</H6>
                            </Grid>
                            {Array.from({ length: selectCount }).map(
                                (_, index) => (
                                    <>
                                        <Grid item xs={9}>
                                            <Select
                                                size="small"
                                                variant="outlined"
                                                name={`workshopIds[${index}]`}
                                                fullWidth
                                                value={
                                                    values.workshopIds[index] ||
                                                    ''
                                                }
                                                onChange={handleChange}
                                            >
                                                {workshopList &&
                                                    workshopList.map(
                                                        (item, key) => (
                                                            <MenuItem
                                                                key={key}
                                                                value={item.id}
                                                            >
                                                                {item.name}
                                                            </MenuItem>
                                                        ),
                                                    )}
                                            </Select>
                                        </Grid>
                                        <Grid
                                            item
                                            xs={2}
                                            onClick={() =>
                                                handleRemoveSelect(index)
                                            }
                                        >
                                            <Button
                                                size="small"
                                                variant="contained"
                                                color="error"
                                            >
                                                Remove
                                            </Button>
                                        </Grid>
                                    </>
                                ),
                            )}
                        </Grid>
                        <Grid item onClick={handleAddSelect}>
                            <Button size="small" variant="contained">
                                Add New Workshop
                            </Button>
                        </Grid>
                    </Grid>

                    <FlexBox justifyContent="flex-end" gap={2} marginTop={4}>
                        <Button fullWidth size="small" variant="outlined">
                            Cancel
                        </Button>
                        <Button
                            fullWidth
                            size="small"
                            type="submit"
                            variant="contained"
                        >
                            Save
                        </Button>
                    </FlexBox>
                </form>
            </Card>
        </Box>
    );
};

export default InvoiceEdit;
